import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, FileText, Scan, Wand2, Package, CheckCircle, 
  ArrowRight, Info, Shield, Clock, Users 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const PIPELINE_STAGES = [
  {
    id: 'upload',
    name: 'Document Upload',
    icon: Upload,
    color: 'blue',
    description: 'You provide your technical document',
    whatHappens: 'File validation, format detection, security scan',
    humanValue: 'No manual reformatting needed',
    duration: '< 1 second'
  },
  {
    id: 'parsing',
    name: 'Content Reading',
    icon: Scan,
    color: 'indigo',
    description: 'We extract and understand your content',
    whatHappens: 'Text extraction, structure analysis, metadata capture',
    humanValue: 'Your content structure is preserved',
    duration: '5-15 seconds'
  },
  {
    id: 'normalized',
    name: 'Structure Understanding',
    icon: FileText,
    color: 'purple',
    description: 'We map your document to standard elements',
    whatHappens: 'Heading hierarchy, section detection, component identification',
    humanValue: 'Intelligent organization without data loss',
    duration: '10-30 seconds'
  },
  {
    id: 'transforming',
    name: 'Standards Application',
    icon: Wand2,
    color: 'pink',
    description: 'We apply your chosen compliance standard',
    whatHappens: 'Keyword validation, compliance checks, metadata enrichment',
    humanValue: 'Automatic compliance without expert knowledge',
    duration: '20-60 seconds'
  },
  {
    id: 'exporting',
    name: 'Format Generation',
    icon: Package,
    color: 'green',
    description: 'We create your standards-compliant package',
    whatHappens: 'XML/ZIP generation, validation, metadata inclusion',
    humanValue: 'Audit-ready output in seconds',
    duration: '5-20 seconds'
  },
  {
    id: 'completed',
    name: 'Ready for Use',
    icon: CheckCircle,
    color: 'emerald',
    description: 'Your compliant documentation is ready',
    whatHappens: 'Download, share, or submit to regulatory bodies',
    humanValue: 'Confidence to submit without expert review',
    duration: 'Instant'
  }
];

const TRUST_FACTORS = [
  {
    icon: Shield,
    title: 'Audit Trail',
    description: 'Every transformation is logged and reversible'
  },
  {
    icon: Clock,
    title: 'Save Time',
    description: 'Hours of manual work become minutes'
  },
  {
    icon: Users,
    title: 'No Expert Required',
    description: 'Standards knowledge built into the process'
  }
];

export default function DocumentPipelineVisualization({ user, logout }) {
  const navigate = useNavigate();
  const [activeStage, setActiveStage] = useState(null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Standari</h1>
                <p className="text-xs text-gray-500">Documentation Pipeline</p>
              </div>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            How Your Documents Become Compliant
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Documentation anxiety comes from not knowing what's happening. 
            This is the exact journey your document takes from upload to compliant output.
          </p>
        </div>

        {/* Trust Factors */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {TRUST_FACTORS.map((factor, index) => {
            const Icon = factor.icon;
            return (
              <Card key={index} className="border-2 border-blue-100">
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{factor.title}</h3>
                  <p className="text-sm text-gray-600">{factor.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Pipeline Visualization */}
        <div className="relative mb-12">
          {/* Connection Lines */}
          <div className="absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-200 via-purple-200 to-green-200" />

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {PIPELINE_STAGES.map((stage, index) => {
              const Icon = stage.icon;
              const isActive = activeStage === stage.id;

              return (
                <div key={stage.id} className="relative">
                  <Card 
                    className={`cursor-pointer transition-all ${
                      isActive 
                        ? 'border-2 border-blue-500 shadow-lg scale-105' 
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                    }`}
                    onMouseEnter={() => setActiveStage(stage.id)}
                    onMouseLeave={() => setActiveStage(null)}
                  >
                    <CardHeader className="pb-3">
                      <div className={`w-16 h-16 bg-${stage.color}-100 rounded-full flex items-center justify-center mx-auto mb-3`}>
                        <Icon className={`h-8 w-8 text-${stage.color}-600`} />
                      </div>
                      <CardTitle className="text-center text-sm">
                        {stage.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-gray-600 text-center mb-3">
                        {stage.description}
                      </p>
                      <Badge className="w-full justify-center bg-gray-100 text-gray-700 text-xs">
                        {stage.duration}
                      </Badge>
                    </CardContent>
                  </Card>

                  {/* Arrow between stages */}
                  {index < PIPELINE_STAGES.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <ArrowRight className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail Panel */}
        {activeStage && (
          <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200">
            <CardContent className="pt-6">
              {(() => {
                const stage = PIPELINE_STAGES.find(s => s.id === activeStage);
                const Icon = stage.icon;
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <div className="flex items-center space-x-2 mb-3">
                        <Icon className={`h-5 w-5 text-${stage.color}-600`} />
                        <h3 className="font-semibold text-gray-900">What Happens</h3>
                      </div>
                      <p className="text-sm text-gray-700">{stage.whatHappens}</p>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-3">
                        <Info className="h-5 w-5 text-blue-600" />
                        <h3 className="font-semibold text-gray-900">Why It Matters</h3>
                      </div>
                      <p className="text-sm text-gray-700">{stage.humanValue}</p>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-3">
                        <Clock className="h-5 w-5 text-purple-600" />
                        <h3 className="font-semibold text-gray-900">Time Required</h3>
                      </div>
                      <p className="text-sm text-gray-700">{stage.duration}</p>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Key Benefits */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Why This Removes Documentation Anxiety
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Visible Progress</h4>
                  <p className="text-sm text-gray-600">
                    You see exactly where your document is at every moment. No black box.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Predictable Outcome</h4>
                  <p className="text-sm text-gray-600">
                    You know what format you'll get and when. No surprises.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Clear Accountability</h4>
                  <p className="text-sm text-gray-600">
                    Every step is logged. You can prove exactly what happened.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Safe from Failure</h4>
                  <p className="text-sm text-gray-600">
                    If something goes wrong, you get a clear explanation—not silence.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Less Cognitive Load</h4>
                  <p className="text-sm text-gray-600">
                    You don't need to understand DITA or S1000D. The system handles it.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Institutional Memory</h4>
                  <p className="text-sm text-gray-600">
                    Your process is documented. When people leave, the knowledge stays.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <Button 
            onClick={() => navigate('/process')}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg"
          >
            Start Processing a Document
          </Button>
          <p className="text-sm text-gray-500 mt-4">
            See this pipeline in action with your own document
          </p>
        </div>
      </main>
    </div>
  );
}
